import { logService } from '@nsftx/seven-client-retail-utils'
import {
  getBetslipLogDetails,
  useTicketsStore,
  TicketResolveError
} from '@nsftx/seven-client-retail-vue-sgp'
import type { BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import errorParser from '@/utils/errorParser'
import i18n from '@/plugins/translationsPlugin'
import type { TicketCheckerOptions } from './interfaces/TicketCheckerOptions'

const TICKET_RESOLVE_STATUS_TIMEOUT_IN_MS = 5_000
export const TICKET_ACTION_PENDING_NOTIFICATION_ID = 'notification_ticket_action_pending'
const LOG_PREFIX = '[ticketCheckerService]'
const { t } = i18n.global

const waitForTicketResolve = (feBetslipId: string) =>
  new Promise<BetslipView>((resolve, reject) => {
    const { isTicketResolved } = useTicketsStore()

    window.setTimeout(() => {
      const resolvedTicket = isTicketResolved(feBetslipId)

      if (resolvedTicket && resolvedTicket.betslip) {
        logService.debug(
          `${LOG_PREFIX} Ticket already resolved by socket, skipping ticket checker result processing.`,
          {
            code: 'ST_TICKET_CHECKER_ALREADY_RESOLVED',
            ...getBetslipLogDetails(resolvedTicket.betslip)
          }
        )
        resolve(resolvedTicket.betslip)
        return
      }

      reject(
        new TicketResolveError(
          `Ticket is not resolved by socket within ${TICKET_RESOLVE_STATUS_TIMEOUT_IN_MS} ms.`,
          'SGP_TICKET_RESOLVE_SOCKET_TIMEOUT_ERROR',
          {
            context: {
              feBetslipId
            }
          }
        )
      )
    }, TICKET_RESOLVE_STATUS_TIMEOUT_IN_MS)
  })

const handleBetslipChecker = async (
  feBetslipId: string,
  onSuccess: TicketCheckerOptions['onResolveSuccess'],
  onFailed: TicketCheckerOptions['onResolveFailed']
) => {
  const { acceptTicket, setFailedTicket, startChecker, endChecker, isActionPending } =
    useTicketsStore()

  logService.debug(`${LOG_PREFIX} Ticket checker started.`, {
    code: 'ST_TICKET_CHECKER_START_SUCCESS',
    feBetslipId
  })

  try {
    const betslip = await startChecker(feBetslipId)
    const betslipStatusPhase = betslip.status.phase

    if (!isActionPending(feBetslipId)) {
      logService.debug(
        `${LOG_PREFIX} Ticket already resolved by socket, skipping ticket checker result processing.`,
        {
          code: 'ST_TICKET_CHECKER_ALREADY_RESOLVED',
          ...getBetslipLogDetails(betslip)
        }
      )
      return
    }

    logService.info(`${LOG_PREFIX} Ticket successfully resolved by ticket checker.`, {
      code: 'ST_TICKET_CHECKER_RESOLVE_SUCCESS',
      ...getBetslipLogDetails(betslip),
      status: betslipStatusPhase
    })

    if (betslipStatusPhase !== 'PLACED') {
      setFailedTicket(feBetslipId)
      endChecker(feBetslipId)
      return
    }

    acceptTicket(feBetslipId, betslip)
    endChecker(feBetslipId)
    onSuccess(betslip)
  } catch (error) {
    const { showNotification } = useNotificationsStore()

    logService.error(`${LOG_PREFIX} Ticket resolve has failed.`, {
      code: 'ST_TICKET_RESOLVE_CHECKER_ERROR',
      ...errorParser.parseUpstream(error)
    })
    showNotification({
      variant: 'error',
      text: [t('ticket_resolve_failed')]
    })

    setFailedTicket(feBetslipId)
    endChecker(feBetslipId)
    onFailed(error)
  }
}

const handleTicketChecker = async (options: TicketCheckerOptions) => {
  const { showNotification } = useNotificationsStore()
  const { activeFeBetslipId, onResolveSuccess, onResolveFailed } = options

  showNotification({
    id: TICKET_ACTION_PENDING_NOTIFICATION_ID,
    variant: 'info',
    text: [t('ticket_pending_action')]
  })

  try {
    await waitForTicketResolve(activeFeBetslipId)
  } catch (error) {
    logService.error(`${LOG_PREFIX} Ticket socket resolve has failed.`, {
      code: 'ST_TICKET_RESOLVE_SOCKET_ERROR',
      ...errorParser.parseUpstream(error)
    })
    handleBetslipChecker(activeFeBetslipId, onResolveSuccess, onResolveFailed).catch(() => {
      showNotification({
        variant: 'error',
        text: [t('ticket_resolve_failed')]
      })
    })
  }
}

export default {
  handleTicketChecker
}
